import type { QuoteStep } from '../types/quote.types';

/**
 * Maps a field-error key's leading path segment to the step that owns it.
 * Keyed on the guard's own rule prefixes (QuotePublicationGuard::rules()) —
 * `dealer`, `dealer.id`, `customer`, `customer.*`, `vehicle`, `vehicle.*`,
 * `product`, `product.id`, `payment_selection` — plus `services`, added by #872.
 *
 * `services` is NOT a `QuotePublicationGuard` rule prefix. It comes from a
 * sibling check, `QuoteValidationService::validateServiceCountBounds()`,
 * called directly from `QuoteLifeCycleManagement::complete()` (#872 D2) — a
 * 422 with `errors.services` means the product's own configured service-count
 * bounds were violated, and the step that owns fixing it is `configuration`.
 *
 * `payment` still has no guard rule today (D3 class (c) exclusion) and is
 * listed only so a future rule naming that prefix has a step to route to
 * without editing this map's shape. `configuration` was originally kept for
 * the same speculative reason — the prediction was that a future rule would
 * be named `configuration` itself. #872 is that future rule, but it surfaces
 * as `services`, not `configuration`, so both keys now route to the
 * `configuration` step for two different reasons: one still speculative, one
 * now real.
 *
 * `payment_selection` (#873's P6 rule) is a REAL guard rule prefix, and it is
 * NOT the same key as the speculative `payment` entry above: `split('.')[0]`
 * yields the whole `payment_selection` key, so #856's `payment` placeholder
 * never covered it. Both route to the `payment` step.
 */
const STEP_FOR_PREFIX: Record<string, QuoteStep> = {
    dealer: 'dealer',
    vehicle: 'vehicle',
    product: 'product',
    configuration: 'configuration',
    customer: 'customer',
    payment: 'payment',
    // Both the service-count-bounds check and P9's service-id check surface as
    // `services` field errors; this one routing rule serves both.
    services: 'configuration',
    // #873. The guard's field is `payment_selection` (QuotePublicationGuard::rules() P6),
    // NOT `payment` — split('.')[0] yields the whole key. The `payment` entry above is
    // #856's placeholder and does not cover it. Both map to the same step.
    payment_selection: 'payment',
    // #1390. Guard rules P7/P8 (`add_ons.*.addOnId`, `add_ons.*.linkedServiceId`)
    // are configured on the same step `services` routes to.
    add_ons: 'configuration',
    // `plan_id` is configuration-owned, same as `services` and `add_ons`, so a
    // pricing refusal naming it routes back to the Configure step.
    plan_id: 'configuration',
};

/**
 * #856. Which journey step owns a field the completion guard (quote-service's
 * `App\Services\QuotePublicationGuard`) refused. Consumed by `useCompleteSale`'s
 * 422 branch to route the user out of the refusal instead of leaving them on
 * `summary` with an opaque field-path error — the exact failure #819 removed
 * from the resume path, reproduced here for completion.
 *
 * `order` is the CALLER's live step order — never a hard-coded copy. Epic
 * 7.1.8 made the journey order per-tenant: `DEALER_FIRST_ORDER` or
 * `DEALER_DEFERRED_ORDER` from `../services/quoteStepOrder.ts`, resolved at
 * runtime by `useQuoteStepOrder`. A tenant that defers dealer selection
 * places `dealer` AFTER `customer`; a function that assumed dealer-first
 * ordering would route a `dealer`+`customer` refusal to `dealer` for that
 * tenant when `customer` is the step the user actually needs to revisit
 * first. We route to the EARLIEST failing step in the supplied `order`, not
 * the first key in the field-errors map: `Validator::errors()` ordering is
 * rule-declaration order, an implementation detail nobody should depend on,
 * and every precondition the guard enforces is a HARD precondition — an
 * unrepaired dealer blocks the customer step's own save — so the earliest
 * one in the live journey is the one to fix first.
 *
 * `useQuoteLoader`'s resume walk (useQuoteLoader.ts:149-155) now performs the
 * same earliest-in-`order`-first walk over the live order — the two are
 * aligned by construction, both reading from `useQuoteStepOrder`/
 * `resolveStepOrder`, not merely by convention. As of #872 the alignment goes
 * one layer deeper for the `configuration` step specifically:
 * `isLoadedStepValid.configuration` (useQuoteLoader.ts) and this module's own
 * `configuration` case (STEP_FOR_PREFIX, above) both ultimately answer to the
 * same threshold: the resume walk calls `minimumConfiguredServices()`
 * directly, and a `services` field error only exists because the backend's
 * own counterpart, `QuoteValidationService::validateServiceCountBounds()`
 * (reading the same product config-settings trio), refused the count.
 * Before #872 the resume walk used a hard-coded `length > 0`, which could
 * disagree with a `min: 0` product; that gap is closed, not just the walk
 * mechanics. `useQuoteLoader` additionally pre-empts that walk with #819's
 * vehicle-registration-date check, because
 * that rule is not expressible by "does a value exist on the loaded payload"
 * the way the rest of its per-step validity checks are; this function needs
 * no equivalent pre-empt because the guard's date rule already surfaces as a
 * `vehicle.*` field error, which `STEP_FOR_PREFIX` maps to `'vehicle'` like
 * any other vehicle-owned field.
 *
 * Returns `null` for a field whose prefix maps to no step (or an empty error
 * map). The caller must then leave the user where they are and still show the
 * server's message. This is the fail-safe: a future guard rule for a field
 * with no owning step degrades to today's behaviour rather than dispatching
 * `undefined` into the Redux slice.
 */
export const stepForCompletionError = (
    fieldErrors: Record<string, string[]>,
    order: readonly QuoteStep[],
): QuoteStep | null => {
    const failingSteps = new Set<QuoteStep>();

    for (const field of Object.keys(fieldErrors)) {
        const prefix = field.split('.')[0];
        const step = STEP_FOR_PREFIX[prefix];
        if (step) {
            failingSteps.add(step);
        }
    }

    for (const step of order) {
        if (failingSteps.has(step)) {
            return step;
        }
    }

    return null;
};
